import styles from "./index.module.css";
import "./default.style.css";
import "./compact.style.css";

import React from "react";
import DataComponent, {executeComponentCallback} from "Core/components/DataComponent";
import PropTypes from "prop-types";
import {
	get,
	set,
	isFunction,
	isPlainObject,
	forOwn,
	forEach,
	find,
	map,
	reject,
	isEqual,
	omit,
	cloneDeep,
	difference
} from "lodash";
import {CSS_UNITS} from "Core/const/global";
import {KEY_VALUE_LIST_DISPLAY_TYPE, KEY_VALUE_LIST_DISPLAY_TYPES} from "./const";
import Button, {BUTTON_DISPLAY_TYPE, BUTTON_DISPLAY_TYPES, BUTTON_STYLES} from "Core/components/display/Button";
import {icon_font_close_symbol, icon_font_create_symbol, icon_font_delete_symbol} from "Config/app";
import KeyValueListItem, {KeyValueListItemDataObject} from "./ListItem";
import {getArray, getBoolean, getString, isset} from "Core/helpers/data";
import FormWrapper from "Core/components/advanced/FormWrapper";
import CheckboxInput from "Core/components/input/CheckboxInput";
import {Tooltip} from "Core/components/global/Tooltip";
import ConfirmDialog from "Core/components/dialogs/ConfirmDialog";
import {closeDialog, openDialog} from "Core/helpers/dialog";
import {v4} from "uuid";

class KeyValueList extends DataComponent {
	constructor(props) {
		super(props, {
			/**
			 * Key/value items
			 * @type {KeyValueListItemDataObject[]}
			 */
			data: [],

			/**
			 * List of selected key/value items
			 * @type {KeyValueListItemDataObject[]}
			 */
			selected: [],
		}, {
			translationPath: 'KeyValueListComponent',
			domPrefix: 'key-value-list-component',
			enableLoadOnDataPropChange: true,
			optimizedUpdate: true,
			optimizedUpdateIgnoreProps: [
				// Ignore 'onChange' because anonymous functions (that are usually used for events) are generated on each 
				// parent component render, so they are always different and optimization will not work. This is true for 
				// any function prop.
				'onChange'
			],
			// Include only state fields relevant for rendering the component
			// @note This component extends and abstract component that might have some state fields.
			optimizedUpdateIncludeState: ['data', 'selected']
		});
		
		// Selection methods
		this.selectItem = this.selectItem.bind(this);
		this.unselectItem = this.unselectItem.bind(this);
		this.toggleSelectItem = this.toggleSelectItem.bind(this);
		this.selectAllItems = this.selectAllItems.bind(this);
		this.unselectAllItems = this.unselectAllItems.bind(this);
		this.toggleAllItems = this.toggleAllItems.bind(this);
		this.unselectMissingItems = this.unselectMissingItems.bind(this);
		this.isItemSelected = this.isItemSelected.bind(this);
		this.areAllItemsSelected = this.areAllItemsSelected.bind(this);

		// Action methods
		this.removeSelectedItems = this.removeSelectedItems.bind(this);
		this.clearItems = this.clearItems.bind(this);

		// Render methods
		this.getStyle = this.getStyle.bind(this);
	}


	// Data methods -----------------------------------------------------------------------------------------------------
	/**
	 * Get data to load into local component's state
	 * @description Create and return data that can be loaded directly into local component's state based on the raw
	 * external data (usually sent through props). In some sense this is a method that maps external data into format
	 * that component can use in its local state. This method should return data in the same format as 'getData' method.
	 * @note This method will not mutate the passed data.
	 *
	 * @param {any} rawData - External data that will be used to create local component's state compatible data.
	 * @return {any|null} Local component's state compatible data or null if data could not be loaded.
	 */
	getDataToLoad(rawData) {
		const {keyField, valueField} = this.props;
		let result = [];

		// Handle array of objects
		if (Array.isArray(rawData)) {
			forEach(getArray(super.getDataToLoad(rawData)), item => {
				result.push(new KeyValueListItemDataObject(
					getString(item, 'GUIID', v4(), true),
					get(item, keyField), 
					get(item, valueField), 
					omit(item, [keyField, valueField])
				));
			});
		} 
		// Handle plain object
		else if (isPlainObject(rawData)) {
			const keyValueObject = super.getDataToLoad(rawData);
			if (keyValueObject) {
				forOwn(keyValueObject, (value, key) => result.push(new KeyValueListItemDataObject(
					'', key, value
				)));
			}
		}
		
		return result;
	}

	/**
	 * Get data that will be returned to the parent component
	 * @description Create and return data that will be returned to paren component (usually through onChange event)
	 * based on the local component's raw state data
	 * @note This method will not mutate the passed data.
	 *
	 * @return {any} Data that will be returned to the parent component.
	 */
	getDataToReturn(data) {
		const {keyField, valueField, returnAsObject} = this.props;
		const dataToUse = cloneDeep(data ? data : this.state.data);
		
		if (returnAsObject) {
			let result = {};
			getArray(dataToUse).forEach(i => { set(result, get(i, 'key'), get(i, 'value')); });
			return result;
		} else {
			return getArray(dataToUse).map(i => ({
				...get(i, 'data', {}),
				GUIID: i.GUIID,
				[keyField]: get(i, 'key'),
				[valueField]: get(i, 'value'),
			}));
		}
	}

	/**
	 * Load data into local component's state and call component's update method if data is different from current data
	 * @note This method will not mutate the passed data. We recommend that you override this method in your child
	 * classes if you need to alter the loading procedure. We do not recommend overriding the 'load' method!
	 *
	 * @param {any} data - Data to load into local component's state. If data is the same as current local component's
	 * state data it will not be loaded and component's update method will not be called.
	 * @return {Promise<object>} Promise that is resolved with entire component's local state after it has been loaded.
	 */
	defaultLoad(data) {
		// Get current and data to load
		const currentData = this.getData();
		const dataToLoad = this.getDataToLoad(data);

		// Get current and data to load that can be compared
		// @note GUI ID is auto-generated in 'getDataToLoad' method, so it should not be compared.
		const currentDataToCompare = getArray(currentData).map(i => omit(i, ['GUIID']));
		const dataToLoadToCompare = getArray(dataToLoad).map(i => omit(i, ['GUIID']));

		// Load data if it is different than current data
		if(!isEqual(currentDataToCompare, dataToLoadToCompare)) {
			return this.setState({data: dataToLoad})
				.then(this.unselectMissingItems);
		}
		// Do not load data if it is not different from current data
		return new Promise(resolve => resolve(this.state));
	}

	/**
	 * Trigger component's onChange event with component's main data as param
	 * @param {Event} [event] - Optional JavaScript event that triggered the update.
	 * @return {any|null} Any return value from 'onChange' event function or null.
	 */
	update(event) {
		this.updateDataHistory().then();
		return executeComponentCallback(this.props.onChange, this.getDataToReturn(), event);
	}


	// Selection methods ------------------------------------------------------------------------------------------------
	/**
	 * Select key/value list item
	 * @param {string|Object} predicate - Selector used to find the item in the list. If predicate is a string it will be 
	 * treated as a GUIID. If predicate is an object it will be used as a standard predicate (for ex.: { id: '123' }).
	 * @return {Promise<object>} Promise that is resolved with entire component's local state after it has been updated.
	 */
	selectItem(predicate) {
		const items = getArray(this.getData());
		
		let item;
		if (typeof predicate === 'string') item = find(items, {GUIID: predicate});
		else if (isPlainObject(predicate)) item = find(items, predicate);
		
		return (item ? this.addStateArrayItem('selected', item) : Promise.resolve(this.state));
	}

	/**
	 * Unselect key/value list item
	 * @param {string|Object} predicate - Selector used to find the item in the list. If predicate is a string it will be
	 * treated as a GUIID. If predicate is an object it will be used as a standard predicate (for ex.: { id: '123' }).
	 * @return {Promise<object>} Promise that is resolved with entire component's local state after it has been updated.
	 */
	unselectItem(predicate) {
		if (typeof predicate === 'string') return this.removeStateArrayItem('selected', {GUIID: predicate});
		else if (isPlainObject(predicate)) return this.removeStateArrayItem('selected', predicate);
	}

	/**
	 * Toggle select for key/value list item
	 * @param {string|Object} predicate - Selector used to find the item in the list. If predicate is a string it will be
	 * treated as a GUIID. If predicate is an object it will be used as a standard predicate (for ex.: { id: '123' }).
	 * @return {Promise<object>} Promise that is resolved with entire component's local state after it has been updated.
	 */
	toggleSelectItem(predicate) {
		let predicateObj = null;
		if (typeof predicate === 'string') predicateObj = {GUIID: predicate};
		else if (isPlainObject(predicate)) predicateObj = predicate;
		
		if (predicateObj) {
			const items = getArray(this.getData());
			const selectedItems = getArray(this.state, 'selected');
			const item = find(items, predicateObj);
			const selectedItem = find(selectedItems, predicateObj);
			if (item) {
				if (selectedItem) return this.removeStateArrayItem('selected', predicateObj);
				else return this.addStateArrayItem('selected', item);
			}
		}
		
		return Promise.resolve(this.state);
	}

	/**
	 * Select all key/value list items
	 * @return {Promise<object>} Promise that is resolved with entire component's local state after it has been updated.
	 */
	selectAllItems() { return this.setState({selected: getArray(this.getData())}); }

	/**
	 * Clear key/value list items selection
	 * @return {Promise<object>} Promise that is resolved with entire component's local state after it has been updated.
	 */
	unselectAllItems() { return this.setState({selected: []}); }

	/**
	 * Toggle key/value list items selection
	 * @return {Promise<object>} Promise that is resolved with entire component's local state after it has been updated.
	 */
	toggleAllItems() {
		const allSelected = this.areAllItemsSelected();
		return (allSelected ? this.unselectAllItems() : this.selectAllItems());
	}

	/**
	 * Unselect key/value pairs that are not in the list
	 * @note Check is performed using items GUI ID ('GUIID').
	 * @return {Promise<object>} Promise that is resolved with entire component's local state after it has been updated.
	 */
	unselectMissingItems() {
		return this.setState({
			selected: getArray(this.state, 'selected')
				.filter(item => map(getArray(this.getData()), 'GUIID').includes(item.GUIID))
		})
	}

	/**
	 * Check if key/value item is selected
	 * 
	 * @param {KeyValueListItemDataObject} item - Item object.
	 * @return {boolean}
	 */
	isItemSelected(item) {
		const selectedItems = getArray(this.state, 'selected');
		return !!find(selectedItems, {GUIID: item.GUIID});
	}

	/**
	 * Check if all key/value items are selected
	 * @return {boolean}
	 */
	areAllItemsSelected() {
		const items = getArray(this.getData());
		const selectedItems = getArray(this.state, 'selected');
		return (!!items.length && !difference(map(items, 'GUIID'), map(selectedItems, 'GUIID')).length);
	}
	

	// Action methods ---------------------------------------------------------------------------------------------------
	/**
	 * Remove selected items
	 */
	removeSelectedItems() {
		if (getBoolean(this.props, 'confirmDeleteSelected')) {
			openDialog(
				'',
				ConfirmDialog,
				{
					message: this.t('remove_items_confirm_message'),
					onYes: GUIID => {
						const selectedItemGUIIDs = map(getArray(this.state, 'selected'), 'GUIID');
						return this.setData(reject(getArray(this.getData()), i => selectedItemGUIIDs.includes(i.GUIID)))
							.then(() => this.unselectAllItems())
							.then(() => this.update())
							.then(() => executeComponentCallback(this.props.onDelete, this.getDataToReturn()))
							.then(() => closeDialog(GUIID));
					},
				}, {
					id: 'confirm-remove-selected',
					closeOnEscape: true,
					closeOnClickOutside: true,
					hideCloseBtn: true,
					maxWidth: 550,
				}
			);
		} else {
			const selectedItemGUIIDs = map(getArray(this.state, 'selected'), 'GUIID');
			return this.setData(reject(getArray(this.getData()), i => selectedItemGUIIDs.includes(i.GUIID)))
				.then(() => this.unselectAllItems())
				.then(() => this.update())
				.then(() => executeComponentCallback(this.props.onDelete, this.getDataToReturn()));
		}
	};

	/**
	 * Remove all items
	 */
	clearItems() {
		if (getBoolean(this.props, 'confirmClear')) {
			openDialog(
				'',
				ConfirmDialog,
				{
					message: this.t('clear_item_confirm_message'),
					onYes: GUIID => {
						this.clearData()
							.then(() => this.setState({selected: []}))
							.then(() => this.update())
							.then(() => executeComponentCallback(this.props.onClear, this.getDataToReturn()))
							.then(() => closeDialog(GUIID));
					},
				}, {
					id: 'confirm-remove-all',
					closeOnEscape: true,
					closeOnClickOutside: true,
					hideCloseBtn: true,
					maxWidth: 550,
				}
			);
		} else {
			this.clearData()
				.then(() => this.setState({selected: []}))
				.then(() => this.update())
				.then(() => executeComponentCallback(this.props.onClear, this.getDataToReturn()));
		}
	}

	/**
	 * Remove item from component's main data in local state
	 * @note This method is used if component's main data is an array of objects.
	 *
	 * @param {Object} predicate - Selector object used to find the item (for example: { id: '123' }).
	 * @param {string, Array<string, 'asc'|'desc'>|function|null} [sort=null] - Sort the items after update. String will
	 * sort items by the field name in the ascending order. Array expects two items: field name to sort by and sort
	 * order. Function should define a custom sort method.
	 * @return {Promise<any>} Promise that is resolved with entire component's local state after data item has been
	 * removed.
	 */
	removeItem(predicate, sort = null) {
		if (getBoolean(this.props, 'confirmDelete')) {
			openDialog(
				'',
				ConfirmDialog,
				{
					message: this.t('remove_item_confirm_message'),
					onYes: GUIID => {
						super.removeItem(predicate)
							.then(() => this.update())
							.then(() => executeComponentCallback(this.props.onDelete, this.getDataToReturn()))
							.then(() => closeDialog(GUIID));
					},
				}, {
					id: 'confirm-remove-item',
					closeOnEscape: true,
					closeOnClickOutside: true,
					hideCloseBtn: true,
					maxWidth: 550,
				}
			);
		} else {
			super.removeItem(predicate)
				.then(() => this.update())
				.then(() => executeComponentCallback(this.props.onDelete, this.getDataToReturn()));
		}
	}
	

	// Render methods ---------------------------------------------------------------------------------------------------
	/**
	 * Get style object for component wrapper element
	 */
	getStyle() {
		const {maxHeight} = this.props; 
		let result = {};
		
		// Get max height
		if (typeof maxHeight === 'string') {
			if (CSS_UNITS.some(i => maxHeight.endsWith(i))) set(result, 'maxHeight', maxHeight);
			else {
				const maxHeightNumber = parseFloat(maxHeight);
				if (!isNaN(maxHeightNumber) && maxHeightNumber > 0) {
					set(result, 'maxHeight', `${maxHeightNumber}px`);
				}
			}
		} else if (typeof maxHeight === 'number' && maxHeight > 0) {
			set(result, 'maxHeight', `${maxHeight}px`);
		}
		
		return result;
	}
	
	render() {
		const {
			styleName, itemStyleName, toolbarStyleName, className, selectable, showLabels, showToolbar, 
			customToolbarButtons, keyColumnClassName, keyLabel, keyDisplayType, keyDisplayOptions, keyCustomComponent, 
			valueColumnClassName, valueLabel, valueDisplayType, valueDisplayOptions, valueCustomComponent, 
			showBottomToolbar, canAdd, canRemove, addLabel, emptyItemData, maxItems, 
		} = this.props;
		const items = getArray(this.getData());
		const hasMaxHeight = get(this.getStyle(), 'maxHeight');
		const hasMaxItems = (isset(maxItems) && Number.isInteger(maxItems));
		
		return (
			<div 
				id={this.getDomId()} 
				className={
					`${this.getOption('domPrefix')} ${styleName}-style ${className} ${styles['wrapper']} ` +
					`${selectable ? `selectable` : `not-selectable`} ${canRemove ? `removable` : `not-removable`}`
				}
				style={this.getStyle()}
			>
				{
					showToolbar ?
						<div 
							className={`toolbar ${toolbarStyleName} ${styles['toolbar']} ${hasMaxHeight ? 'max-height' : ''}`}
						>
							{!canAdd || (hasMaxItems && items.length >= maxItems) ? null :
								<>
									<Tooltip
										tag="span"
										title={!isset(addLabel) ? this.t('Add', 'general') : getString(addLabel)}
										size="small"
										position="top-center"
										arrow={true}
										interactive={false}
									>
										<Button
											displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
											icon={icon_font_create_symbol}
											onClick={() => 
												this.addItem(new KeyValueListItemDataObject(
													undefined, undefined, undefined, emptyItemData
												))
													.then(() => this.update())
													.then(() => executeComponentCallback(this.props.onAdd, this.getDataToReturn()))
											}
										/>
									</Tooltip>
									<div className="separator" />
								</>
							}
							{!canRemove ? null :
								<>
									<Tooltip
										tag="span"
										title={`${this.t('Remove selected', 'general')}...`}
										size="small"
										position="top-center"
										arrow={true}
										interactive={false}
									>
										<Button
											displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
											icon={icon_font_delete_symbol}
											onClick={this.removeSelectedItems}
											disabled={!getArray(this.state, 'selected').length}
										/>
									</Tooltip>
									<div className="separator" />
									<Tooltip
										tag="span"
										title={`${this.t('Remove all', 'general')}...`}
										size="small"
										position="top-center"
										arrow={true}
										interactive={false}
									>
										<Button
											displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
											icon={icon_font_close_symbol}
											onClick={this.clearItems}
											disabled={!getArray(this.getData()).length}
										/>
									</Tooltip>
									<div className="separator" />
								</>
							}
							{getArray(customToolbarButtons).map((btn, idx) => 
								<Button 
									key={idx}
									displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
									{...btn} 
									onClick={e => {
										if (isFunction(btn.onClick)) {
											Promise.resolve(
												btn.onClick(e, this.getDataToReturn(getArray(this.state, 'selected')), items)
											).then(() => this.update());
										}
									}}
								/>
							)}
							{getArray(customToolbarButtons).length ? <div className="separator" /> : null}
						</div>
						: null
				}
				
				{
					showLabels ? 
						<div className={`labels ${styles['labels']} ${!keyLabel && !valueLabel ? 'no-labels' : ''}`}>
							{
								selectable ?
									<div className={`select-wrapper`}>	
										<CheckboxInput
											asButton={true}
											checked={this.areAllItemsSelected()}
											size={18}
											onChange={this.toggleAllItems}
										/>
									</div>
									: null
							}
							{!keyLabel && !valueLabel ? null :
								<>
									<div className={`key-label ${keyColumnClassName}`}>{keyLabel}</div>
									<div className={`value-label ${valueColumnClassName}`}>{valueLabel}</div>
								</>
							}
						</div>
						: null
				}

				{items.length === 0 ? null :
					<FormWrapper 
						className={`item-wrapper ${styles['itemWrapper']} ${hasMaxHeight ? styles['maxHeight'] : ''}`}
					>
						{items.map(i =>
							<KeyValueListItem
								key={i.GUIID}
								className={`keyColumnClassName ${styles['item']}`}
								styleName={itemStyleName}
								showLabels={showLabels}
								keyLabel={keyLabel}
								valueLabel={valueLabel}
								selectable={selectable}
								canRemove={canRemove}
								selected={this.isItemSelected(i)}
								data={i}
								keyDisplayType={keyDisplayType}
								keyDisplayOptions={keyDisplayOptions}
								keyCustomComponent={keyCustomComponent}
								valueDisplayType={valueDisplayType}
								valueDisplayOptions={valueDisplayOptions}
								valueCustomComponent={valueCustomComponent}
								onSelect={this.toggleSelectItem}
								onDelete={GUIID => this.removeItem({GUIID})}
								onChange={(v, event) => this.handleItemChangeAndUpdate({GUIID: i.GUIID}, v, undefined, event)}
							/>
						)}
					</FormWrapper>
				}
				
				{
					showBottomToolbar ?
						!canAdd || (hasMaxItems && items.length >= maxItems) ? null :
						<div className={`bottom-toolbar ${styles['bottomToolbar']}`}>
							<Button
								displayType={BUTTON_DISPLAY_TYPE.NONE}
								icon={icon_font_create_symbol}
								label={!isset(addLabel) ? this.t('Add', 'general') : getString(addLabel)}
								onClick={() => 
									this.addItem(new KeyValueListItemDataObject(
										undefined, undefined, undefined, emptyItemData
									))
										.then(() => this.update())
										.then(() => executeComponentCallback(this.props.onAdd, this.getDataToReturn()))
								}
							/>
						</div>
						: null
				}
			</div>
		);
	}
}


/**
 * Define component's own props that can be passed to it by parent components
 */
KeyValueList.propTypes = {
	// Component style name
	// @description Component style name is a name of the style that will be used to determine the CSS used to style the
	// component.
	styleName: PropTypes.string,
	// List item component style name
	// @description List item component style name is a name of the style that will be used to determine the  CSS used to
	// style the list item component.
	itemStyleName: PropTypes.string,
	// Component toolbar style name
	toolbarStyleName: PropTypes.string,
	// Component's wrapper element id attribute
	id: PropTypes.string,
	// Component's wrapper element class attribute
	className: PropTypes.string,
	// Max. height of the component
	maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	// Flag that determines if key/value pairs will be selectable
	// @note Selectable items can be removed or other actions can be performed on them by adding custom toolbar buttons.
	selectable: PropTypes.bool,
	// Flag that determines if delete single action will prompt a confirm dialog before triggering 'onDelete' event
	confirmDelete: PropTypes.bool,
	// Flag that determines if delete selected action will prompt a confirm dialog before triggering 'onDelete' event
	confirmDeleteSelected: PropTypes.bool,
	// Flag that determines if clear action will prompt a confirm dialog before triggering 'onDelete' event
	confirmClear: PropTypes.bool,
	// Flag that determines if key and value labels will be rendered
	showLabels: PropTypes.bool,
	// Flag that determines if toolbar will be rendered
	showToolbar: PropTypes.bool,
	// Array of custom toolbar buttons
	// @note Custom tab button will be rendered at the end of the default toolbar. Buttons 'onClick' prop will get 
	// additional argument (apart from the click event) that will contain a list of all selected items and a list of all
	// items in that order.
	customToolbarButtons: PropTypes.arrayOf(PropTypes.shape({
		// Button element 'id' attribute.
		id: PropTypes.string,
		// Button element CSS class attribute.
		className: PropTypes.string,
		// The default behavior of the button. Possible values are: 'submit', 'reset' or 'button'.
		type: PropTypes.string,
		// Button display type ('none', 'solid', 'transparent', ...)
		displayType: PropTypes.oneOf(BUTTON_DISPLAY_TYPES),
		// Button display style ('default', 'success', 'error', ...)
		displayStyle: PropTypes.oneOf(BUTTON_STYLES),
		// If true, bigger button will be rendered.
		big: PropTypes.bool,
		// The name of the button, submitted as a pair with the button’s value as part of the form data.
		name: PropTypes.string,
		// Defines the value associated with the button’s name when it’s submitted with the form data. This value is passed
		// to the server in params when the form is submitted.
		value: PropTypes.string,
		// This Boolean attribute specifies that the button should have input focus when the page loads. 
		// @note Only one element in a document can have this attribute.
		autofocus: PropTypes.bool,
		// This Boolean attribute prevents the user from interacting with the button: it cannot be pressed or focused.
		disabled: PropTypes.bool,
		// If true, button will not be rendered.
		hide: PropTypes.bool,
		// Button label rendered as a child of the <button> component before any other child elements but after the icon.
		label: PropTypes.string,
		// Set to true to support HTML in 'label' prop.
		// @warning Be careful when using this flag because it can cause security issues. It uses 'dangerouslySetInnerHTML' 
		// to allow HTML content. 
		allowHtmlLabel: PropTypes.bool,
		// Font icon symbol name.
		icon: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
		// If true, icon will spin (if supported by font icon set used).
		spinIcon: PropTypes.bool,
		// Icon props
		// @see Icon component
		iconProps: PropTypes.object,

		// Events
		onClick: PropTypes.func,
	})),
	// Flag that determines if bottom toolbar will be rendered
	showBottomToolbar: PropTypes.bool,
	// Flag that specifies if new items can be added to the list
	canAdd: PropTypes.bool,
	// Flag that specifies if items can be removed from the list
	canRemove: PropTypes.bool,
	
	// Label used for add buttons
	// @note If undefined, default label will be used.
	addLabel: PropTypes.string,
	
	// Key column wrapper elements class name
	// @note This will be applied to key label wrapper element as well.
	keyColumnClassName: PropTypes.string,
	// Field name of the key if data is an array of plain objects
	keyField: PropTypes.string,
	// Key column label
	keyLabel: PropTypes.any,
	// Key column display type
	keyDisplayType: PropTypes.oneOf(KEY_VALUE_LIST_DISPLAY_TYPES).isRequired,
	// Key column component props
	// @note This will depend on key column display type ('keyDisplayType' prop).
	keyDisplayOptions: PropTypes.object,
	// Key column custom component if display type is custom (KEY_VALUE_LIST_DISPLAY_TYPE_CUSTOM)
	keyCustomComponent: PropTypes.elementType,

	// Value column wrapper elements class name
	// @note This will be applied to value label wrapper element as well.
	valueColumnClassName: PropTypes.string,
	// Field name of the value if data is an array of plain objects
	valueField: PropTypes.string,
	// Value column label
	valueLabel: PropTypes.any,
	// Value column display type
	// TODO: separate constants for value display types
	valueDisplayType: PropTypes.oneOf(KEY_VALUE_LIST_DISPLAY_TYPES).isRequired,
	// Value column component props
	// @note This will depend on value column display type ('keyDisplayType' prop).
	valueDisplayOptions: PropTypes.object,
	// Value column custom component if display type is custom (KEY_VALUE_LIST_DISPLAY_TYPE_CUSTOM)
	valueCustomComponent: PropTypes.elementType,
	
	// Main component data
	// @note Can a plain object where keys will be used as keys and values will be used as values ({id: 5, name: 'Test'})
	// or an array of plain objects where keys and values will be extracted from each object based on 'keyField' and 
	// 'valueFiled' props ([{key: 'id', value: 5}, {key: 'name', value: 'Test'}]).
	data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
	// Additional data used when creating each new element
	// @description This object will be used to set the initial values for all the additional data.
	emptyItemData: PropTypes.object,
	// Flag that specifies if key/value object will be passed to events instead of the KeyValueListItemDataObject array 
	returnAsObject: PropTypes.bool,
	// Max. number of items in the list
	// @note Add buttons will not be rendered if this is defined and the number of items is equal or greater than this.
	maxItems: PropTypes.number,
	
	// Events -----------------------------------------------------------------------------------------------------------
	onChange: PropTypes.func, // Arguments: KeyValueListItemDataObject[]|Object - depending on 'returnAsObject' prop, {Event} [event]
	onAdd: PropTypes.func, // Arguments: KeyValueListItemDataObject[]|Object - depending on 'returnAsObject' prop
	onDelete: PropTypes.func, // Arguments: KeyValueListItemDataObject[]|Object - depending on 'returnAsObject' prop
	onClear: PropTypes.func, // Arguments: KeyValueListItemDataObject[]|Object - depending on 'returnAsObject' prop
};

/**
 * Define component default values for own props
 */
KeyValueList.defaultProps = {
	styleName: 'default',
	itemStyleName: 'default',
	toolbarStyleName: 'standard',
	id: '',
	className: '',
	selectable: true,
	confirmDelete: true,
	confirmDeleteSelected: true,
	confirmClear: true,
	showLabels: true,
	showToolbar: true,
	customToolbarButtons: [],
	showBottomToolbar: true,
	canAdd: true,
	canRemove: true,

	keyColumnClassName: '',
	keyField: 'key',
	keyLabel: '',
	keyDisplayType: KEY_VALUE_LIST_DISPLAY_TYPE.TEXT,
	keyDisplayOptions: null,
	keyCustomComponent: null,

	valueColumnClassName: '',
	valueField: 'value',
	valueLabel: '',
	valueDisplayType: KEY_VALUE_LIST_DISPLAY_TYPE.TEXT,
	valueDisplayOptions: null,
	valueCustomComponent: null,

	returnAsObject: false,
};

export {KeyValueListItemDataObject} from "./ListItem";
export default KeyValueList;